import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { OnboardingAcknowledgeRequest, OnboardingStatusResponse } from '../../_shared/models/onboarding';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  private _hasRedirected = false;
  get hasRedirected(): boolean { return this._hasRedirected; }
  markAsRedirected(): void { this._hasRedirected = true; }

  private _showOnboarding = new BehaviorSubject<boolean>(false);
  showOnboarding$ = this._showOnboarding.asObservable();

  private _onboardingStatus = new BehaviorSubject<OnboardingStatusResponse | null>(null);
  onboardingStatus$ = this._onboardingStatus.asObservable();

  private _lastFetchTime = 0;
  isStatusFresh(thresholdMs = 2000): boolean {
    return this._lastFetchTime > 0 && (Date.now() - this._lastFetchTime) < thresholdMs;
  }

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string
  ) {}

  // Call before profile switch to get fresh data for the new profile
  resetForProfileChange(): void {
    this._hasRedirected = false;
    this._lastFetchTime = 0;
    this._onboardingStatus.next(null);
  }

  getStatus(profileId: number): Observable<OnboardingStatusResponse> {
    const cached = this._onboardingStatus.getValue();
    if (cached !== null) {
      return of(cached);
    }
    return this.refreshStatus(profileId);
  }

  refreshStatus(profileId: number): Observable<OnboardingStatusResponse> {
    return this.http.get<OnboardingStatusResponse>(
      `${this.baseUrl}api/onboarding/status?profileId=${profileId}`,
      { headers: this.headers }
    ).pipe(
      tap(res => {
        this._lastFetchTime = Date.now();
        this._showOnboarding.next(res.showOnboarding);
        this._onboardingStatus.next(res);
      })
    );
  }

  acknowledge(profileId: number): Observable<void> {
    const body: OnboardingAcknowledgeRequest = { profileId };
    return this.http.post<void>(
      `${this.baseUrl}api/onboarding/acknowledge`,
      JSON.stringify(body),
      { headers: this.headers }
    ).pipe(
      tap(() => this._showOnboarding.next(false))
    );
  }
}
